import { FC, useCallback, useState } from "react";
import { observer } from "mobx-react";
import { Button, CircularProgress } from "@mui/material";

import { DownloadIcon } from "../../../icons";

import { useUploadHistoryStore } from "../../../../hooks/useUploadHistoryStore";
import useNotifier from "../../../../hooks/useNotifier";
import { apiService } from "../../../../services/apiService";
import { saveBlob } from "../../../../services/fileDownloadService";
import { currentTimeZone } from "../../../../stores/ObservabilityStore";

const UploadHistoryExportCsvButton: FC = observer(() => {
  const store = useUploadHistoryStore();
  const notifier = useNotifier();

  const [isDownloading, setIsDownloading] = useState(false);

  const handleExport = useCallback(async () => {
    if (isDownloading) return;

    setIsDownloading(true);
    try {
      const response = await apiService.downloadUploadHistoryCsv({
        ...store.fileListQueryParams,
        columns: store.visibleColumnIds,
        tz: currentTimeZone(),
      });

      if (!response.success || !response.data) {
        notifier.showError(
          response.error || "Failed to export the upload history CSV",
        );
        return;
      }

      saveBlob(
        response.data.blob,
        response.data.filename || "claims_dashboard.csv",
      );
    } finally {
      setIsDownloading(false);
    }
  }, [isDownloading, store, notifier]);

  return (
    <Button
      variant="text"
      startIcon={
        isDownloading ? (
          <CircularProgress size={20} aria-label="Exporting CSV" />
        ) : (
          <DownloadIcon size={20} />
        )
      }
      onClick={handleExport}
      disabled={isDownloading}
      sx={(theme) => ({
        color: theme.palette.text.secondary,
        textTransform: "none",
        fontWeight: 400,
        minWidth: "100px",
        whiteSpace: "nowrap",
        "&:hover": {
          backgroundColor: "transparent",
        },
      })}
    >
      Export to CSV
    </Button>
  );
});

export default UploadHistoryExportCsvButton;
